import React, { useContext, useMemo, useState } from "react";
import { useCommercialData } from "../../store/CommercialDataContext";
import { CommercialSearch, EditableCell, SortableHeaderLabel, FilterDropdown } from "./commercialTableParts";
import personLight from "../../assets/icons/person-light.svg";
import brokerIcon from "../../assets/icons/broker.svg";
import stageFilterIcon from "../../assets/icons/stage-filter.svg";
import { commercialRepOptions } from "../../data/portalFormListData";
import {
    TASK_COLUMNS,
    TASK_PRIORITY_OPTIONS,
    distinctTaskBrokers,
    filterTasks,
    sortTasks,
    taskDealPath,
} from "../../utilities/commercialTaskList";

import AuthContext from "../../store/AuthContext";
import { updateCommercialTask } from "../../services/commercialTasks";
import LoadingOverlay from "../shared/LoadingOverlay";

const TaskRow = ({ task, onSaveField }) => (
    <tr className="commercial__row">
        {TASK_COLUMNS.map((col) => {
            const value = col.accessor(task);
            return (
                <td key={col.key} className={`commercial__td commercial__td--${col.key}`}>
                    {col.editable ? (
                        <EditableCell row={task} config={col.editable} onSaveField={onSaveField} />
                    ) : col.isDealLink && task.dealId ? (
                        <a
                            href={taskDealPath(task)}
                            className="commercial__deal-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open deal in a new tab"
                        >
                            <span className="commercial__route">
                                {(Array.isArray(value) ? value : [value]).map((seg, i) => (
                                    <span key={i} className="commercial__route-leg">
                                        {seg}
                                    </span>
                                ))}
                            </span>
                        </a>
                    ) : col.isStacked ? (
                        <span className="commercial__stack">
                            {(Array.isArray(value) ? value : [value]).map((v, i) => (
                                <span key={i} className="commercial__stack-item">
                                    {v}
                                </span>
                            ))}
                        </span>
                    ) : col.isAvinode && value !== "–" ? (
                        <span className="commercial__avinode">{value}</span>
                    ) : (
                        value
                    )}
                </td>
            );
        })}
        <td className="commercial__td commercial__td--spacer" />
    </tr>
);

// --tasks widens the two free-text columns; this table has far fewer columns
// than the Checklist, so Task and Commercial Notes can take the extra room.
const TaskTable = ({ tasks, onSaveField, sort, onSort }) => (
    <div className="commercial__table-scroll">
        <table className="commercial__table commercial__table--tasks">
            <thead>
                <tr>
                    {TASK_COLUMNS.map((col) => (
                        <th key={col.key} className={`commercial__th commercial__th--${col.key}`}>
                            {col.sortable ? <SortableHeaderLabel col={col} sort={sort} onSort={onSort} /> : col.label}
                        </th>
                    ))}
                    <th className="commercial__th commercial__th--spacer" aria-hidden="true" />
                </tr>
            </thead>
            <tbody>
                {tasks.map((task) => (
                    <TaskRow key={task.id} task={task} onSaveField={onSaveField} />
                ))}
            </tbody>
        </table>
    </div>
);

const PortalCommercialTaskList = () => {
    const authCtx = useContext(AuthContext);
    const [search, setSearch] = useState("");
    const [priorities, setPriorities] = useState([]);
    const [commercials, setCommercials] = useState([]);
    const [brokers, setBrokers] = useState([]);
    // Default: priority High → Low, then earliest-created first (handled by
    // sortTasks when key is null).
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    const { tasks: rawTasks, setTasks, loading } = useCommercialData();

    const filtered = useMemo(
        () => filterTasks(rawTasks, { search, priorities, commercials, brokers }),
        [rawTasks, search, priorities, commercials, brokers]
    );
    const tasks = useMemo(() => sortTasks(filtered, sort), [filtered, sort]);

    const brokerOptions = useMemo(() => distinctTaskBrokers(rawTasks), [rawTasks]);

    const toggle = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

    // Cycle a column: unsorted (default order) → asc → desc → unsorted.
    const onSort = (key) =>
        setSort((prev) => {
            if (prev.key !== key) return { key, dir: "asc" };
            if (prev.dir === "asc") return { key, dir: "desc" };
            return { key: null, dir: "asc" };
        });

    const saveField = async (task, payloadKey, value, taskKey, localValue) => {
        try {
            await updateCommercialTask(task.id, payloadKey, value, authCtx?.token);
            setTasks((prev) =>
                prev.map((row) =>
                    row.id === task.id
                        ? {
                              ...row,
                              [taskKey]: payloadKey === "Complete" ? value : (localValue ?? value),
                              ...(payloadKey === "Assigned_Commercial_Exec" ? { commercialRepId: value.id } : {}),
                          }
                        : row
                )
            );
            return true;
        } catch {
            return false;
        }
    };

    return (
        <div className="commercial">
            <div className="commercial__bar">
                <div className="commercial__bar-filters">
                    <FilterDropdown
                        label="Priority"
                        icon={stageFilterIcon}
                        options={TASK_PRIORITY_OPTIONS}
                        selected={priorities}
                        onToggle={toggle(setPriorities)}
                        onClear={() => setPriorities([])}
                    />
                    <FilterDropdown
                        label="Commercial Rep"
                        icon={personLight}
                        options={commercialRepOptions}
                        selected={commercials}
                        onToggle={toggle(setCommercials)}
                        onClear={() => setCommercials([])}
                    />
                    <FilterDropdown
                        label="Broker"
                        icon={brokerIcon}
                        options={brokerOptions}
                        selected={brokers}
                        onToggle={toggle(setBrokers)}
                        onClear={() => setBrokers([])}
                    />
                </div>

                <CommercialSearch
                    value={search}
                    onChange={setSearch}
                    placeholder="SEARCH TASK, REP OR ROUTE"
                    ariaLabel="Search task, rep or route"
                />
            </div>

            {loading && <LoadingOverlay />}

            {!loading && (
                <section className="commercial__section">
                    {tasks.length ? (
                        <TaskTable tasks={tasks} onSaveField={saveField} sort={sort} onSort={onSort} />
                    ) : (
                        <p className="commercial__empty">No tasks.</p>
                    )}
                </section>
            )}
        </div>
    );
};

export default PortalCommercialTaskList;
